import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Heading,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { MarriageProfileService } from "src/rest/marriageProfileService";
import { UseAppToast } from "src/common/useAppToast";
import { fetchAuthedImageObjectUrl } from "src/utils/authImage";
import { useNavigate } from "react-router-dom";
import { AppRoutes } from "src/common/constants/constantEnums";
import LocationPinIcon from "./icons/LocationPinIcon";
import EditPenIcon from "./icons/EditPenIcon";
import DeleteBinIcon from "./icons/DeleteBinIcon";

// ── Types ─────────────────────────────────────────────────────────────

interface MatrimonyProfileRow {
  id: number;
  profilePhoto?: string;
  fullName?: string;
  nativePlace?: string;
  active?: boolean;
  gender?: string;
  dateOfBirth?: string;
  createdBy?: string; // backend will add later
}

interface PagedResponse {
  content: MatrimonyProfileRow[];
  totalPages: number;
  totalElements: number;
  number: number;
  size: number;
}

// ── Constants ─────────────────────────────────────────────────────────

const PAGE_SIZE = 10;

// Super Admin uses ID = 1 for the search endpoint
const SUPER_ADMIN_SEARCH_ID = 0;

// ── Component ─────────────────────────────────────────────────────────

interface AdminMatrimonyProfilesProps {
  onEditProfile?: (profileId: number) => void;
}

const AdminMatrimonyProfiles: React.FC<AdminMatrimonyProfilesProps> = ({ onEditProfile }) => {
  const navigate = useNavigate();
  const { showSuccessToast, showErrorToast } = UseAppToast();

  const [data, setData] = useState<PagedResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  // Bumped after delete to trigger a re-fetch
  const [refreshKey, setRefreshKey] = useState(0);

  // Photo cache: profileId → objectUrl
  const [photoUrls, setPhotoUrls] = useState<Record<number, string>>({});
  const photoUrlsRef = useRef(photoUrls);
  photoUrlsRef.current = photoUrls;

  // Track which profile IDs have already been fetched or are in-flight
  const fetchedPhotoIdsRef = useRef<Set<number>>(new Set());

  // Delete confirmation modal
  const [deleteTarget, setDeleteTarget] = useState<MatrimonyProfileRow | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Keep toast fns in a ref so they never appear in effect deps
  const toastRef = useRef({ showSuccessToast, showErrorToast });
  toastRef.current = { showSuccessToast, showErrorToast };

  // ── Debounce search ──────────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQuery(searchQuery.trim());
    }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // ── Fetch profiles ───────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const fetchProfiles = async () => {
      try {
        setIsLoading(true);
        const params: Record<string, any> = {
          page,
          size: PAGE_SIZE,
        };
        const res = await MarriageProfileService.search(SUPER_ADMIN_SEARCH_ID, params);
        if (!cancelled) setData(res as PagedResponse);
      } catch (err: any) {
        if (!cancelled) {
          toastRef.current.showErrorToast(
            err?.response?.data?.message || err?.message || "Failed to load profiles."
          );
          setData(null);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void fetchProfiles();

    return () => {
      cancelled = true;
    };
  }, [page, refreshKey]);

  // ── Fetch profile photos (auth-protected) ────────────────────────
  const allProfiles = useMemo(() => data?.content ?? [], [data]);

  // Client-side filtering by name/ID (backend doesn't support name search)
  const profiles = useMemo(() => {
    const q = debouncedQuery.toLowerCase();
    if (!q) return allProfiles;
    return allProfiles.filter(
      (p) =>
        (p.fullName || "").toLowerCase().includes(q) ||
        String(p.id).includes(q)
    );
  }, [allProfiles, debouncedQuery]);

  useEffect(() => {
    let cancelled = false;
    const newUrls: Record<number, string> = {};
    const idsAttempted: number[] = [];
    let committed = false;

    (async () => {
      for (const p of allProfiles) {
        if (!p.profilePhoto || cancelled) continue;
        if (fetchedPhotoIdsRef.current.has(p.id)) continue; // already fetched or in-flight
        fetchedPhotoIdsRef.current.add(p.id);
        idsAttempted.push(p.id);
        try {
          const url = await fetchAuthedImageObjectUrl(p.profilePhoto);
          if (cancelled) {
            URL.revokeObjectURL(url);
            return;
          }
          newUrls[p.id] = url;
        } catch {
          // skip failed photos — remove from tracked set so retry is possible
          fetchedPhotoIdsRef.current.delete(p.id);
        }
      }
      if (!cancelled && Object.keys(newUrls).length > 0) {
        committed = true;
        setPhotoUrls((prev) => ({ ...prev, ...newUrls }));
      }
    })();

    return () => {
      cancelled = true;
      // Only revoke URLs that were never committed to state
      if (!committed) {
        Object.values(newUrls).forEach((u) => URL.revokeObjectURL(u));
      }
      // Remove IDs that never completed so they can be retried
      idsAttempted.forEach((id) => {
        if (!newUrls[id]) fetchedPhotoIdsRef.current.delete(id);
      });
    };
  }, [allProfiles]);

  // Cleanup all photo URLs on unmount
  useEffect(() => {
    return () => {
      Object.values(photoUrlsRef.current).forEach((u) => URL.revokeObjectURL(u));
    };
  }, []);

  // ── Delete handler ───────────────────────────────────────────────
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setIsDeleting(true);
      const res = await MarriageProfileService.deleteProfile(deleteTarget.id);
      const status = (res as any)?.status;
      if (typeof status === "number" && (status < 200 || status >= 300)) {
        throw new Error(`Delete failed (HTTP ${status})`);
      }
      showSuccessToast("Profile deleted successfully.");
      setDeleteTarget(null);
      // Bump refreshKey to trigger a re-fetch
      setRefreshKey((k) => k + 1);
    } catch (err: any) {
      showErrorToast(err?.response?.data?.message || err?.message || "Failed to delete profile.");
    } finally {
      setIsDeleting(false);
    }
  };

  // ── Edit handler ─────────────────────────────────────────────────
  const handleEdit = (profileId: number) => {
    if (onEditProfile) {
      onEditProfile(profileId);
    } else {
      navigate(`${AppRoutes.MATRIMONY_PROFILE_DETAILS}?id=${encodeURIComponent(String(profileId))}`);
    }
  };

  // ── Pagination ───────────────────────────────────────────────────
  const totalPages = data?.totalPages ?? 1;
  const totalElements = data?.totalElements ?? 0;
  const isSearchActive = debouncedQuery.length > 0;

  const startItem = isSearchActive ? (profiles.length > 0 ? 1 : 0) : page * PAGE_SIZE + 1;
  const endItem = isSearchActive ? profiles.length : Math.min((page + 1) * PAGE_SIZE, totalElements);
  const displayTotal = isSearchActive ? profiles.length : totalElements;

  const goToPage = (p: number) => {
    if (p < 0 || p >= totalPages) return;
    setPage(p);
  };

  const paginationButtons = useMemo(() => {
    const buttons: (number | "...")[] = [];
    if (totalPages <= 7) {
      for (let i = 0; i < totalPages; i++) buttons.push(i);
    } else {
      buttons.push(0);
      if (page > 2) buttons.push("...");
      const start = Math.max(1, page - 1);
      const end = Math.min(totalPages - 2, page + 1);
      for (let i = start; i <= end; i++) buttons.push(i);
      if (page < totalPages - 3) buttons.push("...");
      buttons.push(totalPages - 1);
    }
    return buttons;
  }, [page, totalPages]);

  // ── Render ───────────────────────────────────────────────────────
  return (
    <Box>
      {/* Header row */}
      <Flex
        align="center"
        justify="space-between"
        mb={6}
        flexWrap="wrap"
        gap={3}
      >
        <Heading size="lg" color="#4A2C2A">
          Matrimony Profiles
        </Heading>

        <InputGroup maxW="300px">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Search Name/ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            bg="white"
            borderRadius="10px"
            borderColor="#E5E7EB"
          />
        </InputGroup>
      </Flex>

      {/* Table */}
      <Box
        bg="white"
        borderRadius="12px"
        border="1px solid #C6D0DC"
        overflow="hidden"
      >
        <TableContainer>
          <Table variant="simple" size="md">
            <Thead>
              <Tr>
                <Th color="#6B7280" fontSize="xs" textTransform="uppercase" fontWeight="600">
                  Profile
                </Th>
                <Th color="#6B7280" fontSize="xs" textTransform="uppercase" fontWeight="600">
                  Location
                </Th>
                <Th color="#6B7280" fontSize="xs" textTransform="uppercase" fontWeight="600">
                  Status
                </Th>
                <Th color="#6B7280" fontSize="xs" textTransform="uppercase" fontWeight="600">
                  Created By
                </Th>
                <Th color="#6B7280" fontSize="xs" textTransform="uppercase" fontWeight="600" textAlign="center">
                  Action
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {isLoading ? (
                Array.from({ length: PAGE_SIZE }).map((_, i) => (
                  <Tr key={`skel-${i}`}>
                    <Td><Skeleton h="20px" w="160px" /></Td>
                    <Td><Skeleton h="20px" w="120px" /></Td>
                    <Td><Skeleton h="20px" w="60px" /></Td>
                    <Td><Skeleton h="20px" w="80px" /></Td>
                    <Td><Skeleton h="20px" w="60px" /></Td>
                  </Tr>
                ))
              ) : profiles.length === 0 ? (
                <Tr>
                  <Td colSpan={5} textAlign="center" py={10}>
                    <Text color="#6B7280">No matrimony profiles found.</Text>
                  </Td>
                </Tr>
              ) : (
                profiles.map((p) => (
                  <Tr key={p.id} _hover={{ bg: "#FAFAF9" }}>
                    {/* Profile column */}
                    <Td>
                      <HStack spacing={3}>
                        <Avatar
                          size="sm"
                          name={p.fullName || "?"}
                          src={photoUrls[p.id] || undefined}
                        />
                        <Box>
                          <Text fontWeight="600" color="#1F2937" fontSize="sm">
                            {p.fullName || "—"}
                          </Text>
                          <Text fontSize="xs" color="#9CA3AF">
                            M{p.id}
                          </Text>
                        </Box>
                      </HStack>
                    </Td>

                    {/* Location */}
                    <Td>
                      <HStack spacing={1} color="#6B7280" fontSize="sm">
                        <LocationPinIcon />
                        <Text>{p.nativePlace || "—"}</Text>
                      </HStack>
                    </Td>

                    {/* Status */}
                    <Td>
                      <Badge
                        px={3}
                        py={1}
                        borderRadius="full"
                        fontSize="xs"
                        fontWeight="600"
                        colorScheme={p.active ? "green" : "red"}
                        variant="subtle"
                      >
                        {p.active ? "Active" : "Inactive"}
                      </Badge>
                    </Td>

                    {/* Created By (placeholder until backend adds field) */}
                    <Td>
                      <Text fontSize="sm" color="#6B7280">
                        {p.createdBy || "—"}
                      </Text>
                    </Td>

                    {/* Actions */}
                    <Td>
                      <HStack spacing={2} justify="center">
                        <Tooltip label="Edit Profile" hasArrow>
                          <IconButton
                            aria-label="Edit profile"
                            icon={<EditPenIcon />}
                            size="sm"
                            variant="ghost"
                            color="#6B7280"
                            _hover={{ color: "#4A2C2A", bg: "rgba(74,44,42,0.06)" }}
                            onClick={() => handleEdit(p.id)}
                          />
                        </Tooltip>
                        <Tooltip label="Delete Profile" hasArrow>
                          <IconButton
                            aria-label="Delete profile"
                            icon={<DeleteBinIcon />}
                            size="sm"
                            variant="ghost"
                            color="#6B7280"
                            _hover={{ color: "#E53E3E", bg: "rgba(229,62,62,0.06)" }}
                            onClick={() => setDeleteTarget(p)}
                          />
                        </Tooltip>
                      </HStack>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </TableContainer>

        {/* Pagination footer */}
        {!isLoading && totalElements > 0 && (
          <Flex
            align="center"
            justify="space-between"
            px={6}
            py={4}
            borderTop="1px dashed #E5E7EB"
            flexWrap="wrap"
            gap={2}
          >
            <Text fontSize="sm" color="#6B7280">
              Showing {startItem} to {endItem} of {displayTotal} profiles
            </Text>

            <HStack spacing={1}>
              <Button
                size="sm"
                variant="ghost"
                isDisabled={page === 0}
                onClick={() => goToPage(page - 1)}
                color="#6B7280"
              >
                ‹
              </Button>

              {paginationButtons.map((btn, idx) =>
                btn === "..." ? (
                  <Text key={`dots-${idx}`} px={2} fontSize="sm" color="#9CA3AF">
                    ...
                  </Text>
                ) : (
                  <Button
                    key={btn}
                    size="sm"
                    variant={btn === page ? "solid" : "ghost"}
                    bg={btn === page ? "#4A2C2A" : undefined}
                    color={btn === page ? "white" : "#6B7280"}
                    _hover={{ bg: btn === page ? "#4A2C2A" : "#F3F4F6" }}
                    borderRadius="8px"
                    onClick={() => goToPage(btn as number)}
                  >
                    {(btn as number) + 1}
                  </Button>
                ),
              )}

              <Button
                size="sm"
                variant="ghost"
                isDisabled={page >= totalPages - 1}
                onClick={() => goToPage(page + 1)}
                color="#6B7280"
              >
                ›
              </Button>
            </HStack>
          </Flex>
        )}
      </Box>

      {/* Delete confirmation modal */}
      <Modal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} isCentered size="sm">
        <ModalOverlay />
        <ModalContent borderRadius="16px">
          <ModalHeader>Delete Profile</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text color="#4A5568">
              Are you sure you want to delete{" "}
              <Text as="span" fontWeight="700">
                {deleteTarget?.fullName || "this profile"}
              </Text>
              ? This action cannot be undone.
            </Text>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button
                variant="outline"
                onClick={() => setDeleteTarget(null)}
                borderColor="#E5E7EB"
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={confirmDelete}
                isLoading={isDeleting}
              >
                Delete
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default AdminMatrimonyProfiles;
